<template>
  <v-expansion-panels>
    <v-expansion-panel>
      <v-expansion-panel-title disable-icon-rotate>
        {{ title }}
        <template #actions>
          <CounterBadgeIcon
            :counter="selectedCount"
            icon="mdi-account-box-outline"
          />
        </template>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <div v-if="loading" class="text-center pa-4">
          <v-progress-circular indeterminate />
        </div>
        <div v-else-if="!traits.length" class="text-center pa-4">
          Geen kenmerken beschikbaar
        </div>
        <v-list v-else density="compact" lines="two">
          <template v-for="(category, i) in traits" :key="i">
            <v-list-group
              v-if="category && category.children && category.children.length"
            >
              <template #activator="{ props }">
                <v-list-item
                  v-bind="props"
                  :title="category.name"
                ></v-list-item>
              </template>
              <v-list-item
                v-for="trait in category.children"
                :key="trait.id"
                :value="trait.id"
                :class="{ 'selected-trait': isSelected(trait.id) }"
                @click="toggleTraitSelection(trait.id)"
              >
                <template #prepend>
                  <v-checkbox
                    :model-value="isSelected(trait.id)"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                </template>
                <v-list-item-title>{{ trait.name }}</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </template>
        </v-list>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import CounterBadgeIcon from "../ui/CounterBadgeIcon.vue";
import { useTraitsStoreRefactor } from "../../stores/traitsStoreRefactor";
import { useSelectedTraitsStoreRefactor } from "../../stores/selectedTraitsStoreRefactor";

export default {
  name: "ReportTraitSelect",
  components: { CounterBadgeIcon },
  props: {
    preselected: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "Kenmerken",
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    account_id: {
      type: [Number, String],
      required: false,
    },
  },
  emits: ["update:modelValue", "change"],
  data: () => ({
    prev_selected_ids: [],
    selected_ids: [],
    traitsStore: null,
    selectedTraitsStore: null,
    traitsList: [],
    loading: true,
    initialLoadComplete: false,
  }),
  computed: {
    traits() {
      // Group traits by category using dynamic categories
      const traitData = this.traitsList || [];

      // Get unique category labels from the traits
      const categoryLabels = [
        ...new Set(traitData.map((item) => item.category).filter(Boolean)),
      ];

      let trait_category = [];

      for (let i = 0; i < categoryLabels.length; i++) {
        const categoryLabel = categoryLabels[i];
        if (categoryLabel) {
          const categoryChildren = traitData.filter(
            (trait) => trait.category === categoryLabel,
          );
          if (categoryChildren.length > 0) {
            trait_category.push({
              id: i * 100, // Use a computed ID for the category group
              name: categoryLabel,
              children: categoryChildren.map((trait) => ({
                id: trait.id,
                name: trait.name,
                category: trait.category,
              })),
            });
          }
        }
      }

      return trait_category;
    },
    selectedCount() {
      return this.modelValue ? this.modelValue.length : 0;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        console.log("ReportTraitSelect: modelValue changed:", newValue);
      },
    },
    account_id: {
      immediate: true,
      async handler(newAccountId) {
        if (newAccountId && this.initialLoadComplete) {
          await this.prePopulateTraits();
        }
      },
    },
  },
  async mounted() {
    try {
      this.loading = true;
      this.traitsStore = useTraitsStoreRefactor();
      this.selectedTraitsStore = useSelectedTraitsStoreRefactor();

      console.log("ReportTraitSelect: Fetching traits from API...");
      const traitsData = await this.traitsStore.fetchAll();

      // Ensure we have the correct trait data structure
      // Filter out inactive traits - only show active traits for selection
      if (Array.isArray(traitsData)) {
        this.traitsList = traitsData
          .filter((trait) => trait && trait.active === true)
          .map((trait) => ({
            id: trait.id,
            name: trait.name,
            category: trait.category,
          }));
      } else {
        console.warn("Traits data is not an array:", traitsData);
        this.traitsList = [];
      }

      this.initialLoadComplete = true;

      // Pre-populate traits if account_id is provided and no traits are already selected
      if (
        this.account_id &&
        (!this.modelValue || this.modelValue.length === 0)
      ) {
        await this.prePopulateTraits();
      }
    } catch (error) {
      console.error("Error loading traits:", error);
      this.traitsList = [];
      if (!this.modelValue || this.modelValue.length === 0) {
        this.$emit("update:modelValue", []);
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    isSelected(id) {
      if (!Array.isArray(this.modelValue)) return false;
      const target = Number(id);
      const values = this.modelValue
        .map((v) => Number(v))
        .filter((v) => !Number.isNaN(v));
      return values.includes(target);
    },
    toggleTraitSelection(id) {
      if (id === undefined || id === null) return;

      const target = Number(id);
      if (Number.isNaN(target)) return;

      let newValues = Array.isArray(this.modelValue)
        ? this.modelValue.map((v) => Number(v)).filter((v) => !Number.isNaN(v))
        : [];

      const index = newValues.indexOf(target);
      if (index === -1) {
        newValues.push(target);
      } else {
        newValues.splice(index, 1);
      }

      this.$emit("update:modelValue", newValues);
      this.$emit("change");
    },
    async prePopulateTraits() {
      if (!this.account_id || !this.selectedTraitsStore) {
        return;
      }

      try {
        console.log(
          "ReportTraitSelect: Pre-populating traits for account:",
          this.account_id,
        );

        // Fetch fresh selected traits for the account
        await this.selectedTraitsStore.fetchTraitsByAccount(this.account_id);

        // Get the selected trait IDs for form pre-population
        const selectedTraitIds = this.selectedTraitsStore.getSelectedTraitIds(
          this.account_id,
        );

        console.log(
          "ReportTraitSelect: Found selected trait IDs:",
          selectedTraitIds,
        );

        if (selectedTraitIds.length > 0) {
          this.$emit("update:modelValue", selectedTraitIds);
          this.$emit("change");
          console.log(
            "ReportTraitSelect: Pre-populated with traits:",
            selectedTraitIds,
          );
        }
      } catch (error) {
        console.error("ReportTraitSelect: Error pre-populating traits:", error);
      }
    },
  },
};
</script>

<style>
.selected-trait {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
